<template>
	<ClientTeleport>
		<Transition name="fade">
			<div
				v-if="model"
				class="fixed inset-x-0 bottom-4 sm:bottom-8 lg:bottom-12 z-popup pointer-events-none px-4 flex justify-center"
			>
				<div
					class="pointer-events-auto flex items-center gap-2 w-full max-w-fit rounded-2.5xl bg-background-noticeable shadow-gray-md border border-border-secondary px-4 py-3 sm:py-4"
					role="status"
					aria-live="polite"
				>
					<div
						v-if="icon"
						class="shrink-0 flex items-center justify-center rounded-full w-5 h-5"
						:class="iconWrapperClass"
					>
						<NuxtIcon
							:name="icon"
							filled
							class="text-sm text-icon-inverse"
						/>
					</div>
					<div class="flex-1 min-w-0">
						<p class="typo-body-m text-body-primary">
							{{ model.title }}<strong v-if="model.highlight" class="ml-1">{{ model.highlight }}</strong>
						</p>
						<p v-if="model.message" class="typo-body-s text-body-secondary">
							{{ model.message }}
						</p>
					</div>
					<button
						v-if="model.action"
						type="button"
						class="shrink-0 btn btn-secondary btn-xs flex items-center gap-1.5 whitespace-nowrap"
						@click="handleAction"
					>
						<NuxtIcon
							v-if="model.action.icon"
							:name="model.action.icon"
							filled
							class="text-sm text-icon-inverse"
						/>
						{{ model.action.label }}
					</button>
					<button
						type="button"
						class="shrink-0 p-1 text-icon-primary hover:text-icon-active transition-colors rounded-full focus-outline"
						aria-label="Zavřít"
						@click="closeNotification"
					>
						<NuxtIcon name="x" filled class="text-xl" />
					</button>
				</div>
			</div>
		</Transition>
	</ClientTeleport>
</template>

<script setup lang="ts">
	import {type ToastNotification, ToastNotificationType} from '~/types/toastNotification';
	import {useToastNotificationStore} from '~/stores/toastNotification';
	import {TOAST_NOTIFICATION_DURATION} from '#shared/constants';

	const props = defineProps<{
		model: ToastNotification;
	}>();

	const {removeToastNotification} = useToastNotificationStore();

	const icon = computed(() => {
		switch (props.model.type) {
			case ToastNotificationType.WARNING:
				return 'alert-circle-inverted';
			case ToastNotificationType.SUCCESS:
				return 'check';
			default:
				return null;
		}
	});

	const iconWrapperClass = computed(() => (
		props.model.type === ToastNotificationType.SUCCESS
			? 'bg-functional-success-primary'
			: 'bg-functional-warning-primary'
	));

	const {stop: stopDismissTimer} = useTimeoutFn(
		() => removeToastNotification(props.model),
		TOAST_NOTIFICATION_DURATION,
		{immediate: true},
	);

	function dismissNotification() {
		stopDismissTimer();
		removeToastNotification(props.model);
	}

	function closeNotification() {
		dismissNotification();
	}

	async function handleAction() {
		await props.model.action?.handler();
		dismissNotification();
	}
</script>
